import React, { useEffect, useState } from 'react';
import { Card, Button, Alert, Form, Row, Col } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { DocumentInstance, DocumentTemplateField, DocumentAnswer } from '../types';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import { downloadFile } from '../utils/fileDownload';
import MemorialDownloadButtons from '../components/documents/MemorialDownloadButtons';
import { isMemorialDescritivoInstance } from '../utils/memorialDescritivo';
import { parseCustomFieldFilePathsJson } from '../utils/customFieldFileDisplay';
import { shouldShowConditionalField } from '../utils/conditionalField';
import { formatCurrencyInputDisplay, normalizeCurrencyInput } from '../utils/currencyInput';
import {
  ATTACHMENT_ACCEPT,
  validateAttachmentFile,
  validateAttachmentFiles,
} from '../utils/uploadValidation';

interface ScoreFieldProps {
  field: DocumentTemplateField;
  value: string;
  canEdit: boolean;
  onFieldChange: (fieldId: number, value: any) => void;
}

const ScoreField: React.FC<ScoreFieldProps> = ({ field, value, canEdit, onFieldChange }) => {
  // Parsear opções do campo
  let scoreOptions: Array<{ label: string; weight: number }> = [];
  if (field.options) {
    try {
      const parsed = JSON.parse(field.options);
      if (Array.isArray(parsed)) {
        scoreOptions = parsed;
      }
    } catch {
      // Fallback: se não for JSON, tratar como lista simples com peso 1
      scoreOptions = (field.options || '')
        .split(/[,\n]/)
        .map((opt) => opt.trim())
        .filter((opt) => opt.length > 0)
        .map((opt) => ({ label: opt, weight: 1 }));
    }
  }

  // Parsear resposta salva
  let savedRatings: Record<string, number> = {};
  let savedWeights: Record<string, number> = {};
  let savedFinalScore: number | null = null;
  
  if (value) {
    try {
      const parsed = JSON.parse(value);
      if (parsed.ratings) savedRatings = parsed.ratings;
      if (parsed.weights) savedWeights = parsed.weights;
      if (parsed.final_score !== undefined) savedFinalScore = parsed.final_score;
    } catch {
      // Ignorar erro de parse
    }
  }

  // Estado local para notas
  const [ratings, setRatings] = useState<Record<string, number>>(savedRatings);

  // Calcular score final (média ponderada)
  const calculateFinalScore = (currentRatings: Record<string, number>) => {
    let totalWeighted = 0;
    let totalWeight = 0;
    
    scoreOptions.forEach((opt) => {
      const rating = currentRatings[opt.label];
      if (rating !== undefined && rating >= 1 && rating <= 5) {
        totalWeighted += rating * opt.weight;
        totalWeight += opt.weight;
      }
    });
    
    if (totalWeight === 0) return null;
    return totalWeighted / totalWeight;
  };

  const finalScore = calculateFinalScore(ratings);

  // Handler para mudança de nota
  const handleRatingChange = (optionLabel: string, rating: number) => {
    const newRatings = { ...ratings, [optionLabel]: rating };
    setRatings(newRatings);
    
    // Calcular score final
    const newFinalScore = calculateFinalScore(newRatings);
    
    // Preparar dados para salvar
    const weightsObj: Record<string, number> = {};
    scoreOptions.forEach((opt) => {
      weightsObj[opt.label] = opt.weight;
    });
    
    const dataToSave = {
      ratings: newRatings,
      weights: weightsObj,
      final_score: newFinalScore
    };
    
    onFieldChange(field.id, JSON.stringify(dataToSave));
  };

  // Reidratar notas ao carregar
  useEffect(() => {
    if (Object.keys(savedRatings).length > 0) {
      setRatings(savedRatings);
    }
  }, [value]);

  return (
    <div>
      {scoreOptions.map((opt, idx) => {
        const currentRating = ratings[opt.label] || 0;
        return (
          <div key={`${field.id}-${idx}`} className="d-flex align-items-center mb-2">
            <div className="me-3" style={{ minWidth: '200px' }}>
              <strong>{opt.label}</strong>
              <span className="text-muted ms-2">(peso: {opt.weight})</span>
            </div>
            <Form.Select
              value={currentRating || ''}
              onChange={(e) => {
                const rating = Number(e.target.value);
                if (rating >= 1 && rating <= 5) {
                  handleRatingChange(opt.label, rating);
                }
              }}
              disabled={!canEdit}
              style={{ width: '120px' }}
            >
              <option value="">Selecione...</option>
              {[1, 2, 3, 4, 5].map((num) => (
                <option key={num} value={num}>
                  {num}
                </option>
              ))}
            </Form.Select>
            {currentRating > 0 && (
              <span className="ms-2 text-muted">
                Nota: {currentRating}/5
              </span>
            )}
          </div>
        );
      })}
      {finalScore !== null && (
        <Alert variant="info" className="mt-3">
          <strong>Score Final: {finalScore.toFixed(2)}</strong>
          <div className="text-muted small mt-1">
            Média ponderada das notas (1-5)
          </div>
        </Alert>
      )}
      {scoreOptions.length > 0 && Object.keys(ratings).length < scoreOptions.length && (
        <Alert variant="warning" className="mt-2">
          <small>Preencha todas as opções para calcular o score final.</small>
        </Alert>
      )}
    </div>
  );
};

const DocumentEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { showAlert, showConfirm } = useModal();
  const [instance, setInstance] = useState<DocumentInstance | null>(null);
  const [templateFields, setTemplateFields] = useState<DocumentTemplateField[]>([]);
  const [answers, setAnswers] = useState<DocumentAnswer[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [draftValues, setDraftValues] = useState<Record<number, any>>({});
  const [titleDraft, setTitleDraft] = useState('');
  const [opportunityDraft, setOpportunityDraft] = useState('');

  useEffect(() => {
    if (id) {
      fetchDocumentInstance();
    }
  }, [id]);

  const fetchDocumentInstance = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/documents/instances/${id}`);
      const instanceData = response.data;
      setInstance(instanceData);
      setTitleDraft(instanceData.title || '');
      setOpportunityDraft(instanceData.opportunity || '');

      // Buscar campos do template
      const fieldsResponse = await api.get(`/api/documents/types/${instanceData.document_type_id}/fields`);
      setTemplateFields(fieldsResponse.data);

      // Buscar respostas existentes
      const answersResponse = await api.get(`/api/documents/instances/${instanceData.id}/answers`);
      const loadedAnswers = answersResponse.data;
      setAnswers(loadedAnswers);

      const initialDrafts: Record<number, any> = {};
      loadedAnswers.forEach((answer: DocumentAnswer) => {
        initialDrafts[answer.template_field_id] = answer.text_value ?? answer.file_path ?? answer.file_paths ?? '';
      });
      setDraftValues(initialDrafts);
      setTitleDraft(instanceData.title || '');
    } catch (error: any) {
      setError(error.response?.data?.detail || error.message || 'Erro ao carregar documento');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = async (fieldId: number, value: any) => {
    if (!instance) return;

    try {
      setError(null);
      const templateField = templateFields.find(f => f.id === fieldId);
      if (!templateField) return;

      // Verificar se já existe uma resposta para este campo
      const existingAnswer = answers.find(a => a.template_field_id === fieldId);

      let answerData: any = {
        document_instance_id: instance.id,
        template_field_id: fieldId
      };

      // Preparar dados baseado no tipo de campo
      if (templateField.field_type === 'file' || templateField.field_type === 'files') {
        if (typeof value === 'string') {
          answerData.file_path = value;
        } else if (Array.isArray(value)) {
          answerData.file_paths = JSON.stringify(value);
        }
      } else {
        answerData.text_value = typeof value === 'string' ? value : JSON.stringify(value);
      }

      // A gravação não devolve a resposta salva (PUT responde 204 e POST só confirma
      // o id), então o estado local é montado a partir do que foi enviado.
      const savedAt = new Date().toISOString();
      let savedAnswer: DocumentAnswer;
      if (existingAnswer) {
        // Atualizar resposta existente
        await api.put(`/api/documents/instances/${instance.id}/answers/${existingAnswer.id}`, answerData);
        savedAnswer = { ...existingAnswer, ...answerData, updated_at: savedAt };
      } else {
        // Criar nova resposta
        const response = await api.post(`/api/documents/instances/${instance.id}/answers`, answerData);
        savedAnswer = {
          ...answerData,
          id: response.data.id,
          document_instance_id: instance.id,
          template_field_id: fieldId,
          created_at: savedAt,
          updated_at: savedAt,
        };
      }

      // Atualizar lista local de respostas sem refetch completo
      setAnswers((prev) => {
        const existingIndex = prev.findIndex((a) => a.id === savedAnswer.id || a.template_field_id === savedAnswer.template_field_id);
        if (existingIndex >= 0) {
          const next = [...prev];
          next[existingIndex] = savedAnswer;
          return next;
        }
        return [...prev, savedAnswer];
      });

    } catch (error: any) {
      setError(error.response?.data?.detail || error.message || 'Erro ao salvar campo');
    }
  };

  // Descarrega no servidor o que ainda não foi gravado no blur, para que o
  // salvamento explícito enxergue o documento já completo.
  const persistDraftFields = async (): Promise<void> => {
    if (!instance) return;
    for (const field of templateFields) {
      if (field.field_type === 'instruction_text') continue;
      const answer = answers.find((a) => a.template_field_id === field.id);
      const persistedValue = answer?.text_value ?? answer?.file_path ?? answer?.file_paths ?? '';
      const nextValue = draftValues[field.id] ?? persistedValue;
      const normalizedNext = typeof nextValue === 'string' ? nextValue : JSON.stringify(nextValue ?? '');
      const normalizedPersisted =
        typeof persistedValue === 'string' ? persistedValue : JSON.stringify(persistedValue ?? '');
      if (normalizedNext !== normalizedPersisted) {
        await handleFieldChange(field.id, nextValue);
      }
    }
  };

  // A gravação responde 204, então campos definidos pelo servidor (revision do
  // memorial, finalized_at) só chegam relendo a instância.
  const reloadInstance = async (): Promise<DocumentInstance | null> => {
    if (!instance) return null;
    const response = await api.get(`/api/documents/instances/${instance.id}`);
    const reloaded: DocumentInstance = response.data;
    setInstance(reloaded);
    return reloaded;
  };

  const handleSaveDraft = async () => {
    if (!instance) return;

    try {
      setSaving(true);
      setError(null);

      await persistDraftFields();

      await api.put(`/api/documents/instances/${instance.id}`, {
        status: 'draft',
        title: titleDraft || instance.title,
        opportunity: opportunityDraft.trim() || null,
        record_revision: isMemorialDescritivoInstance(instance),
      });

      const reloaded = await reloadInstance();
      setTitleDraft(reloaded?.title || '');
      setOpportunityDraft(reloaded?.opportunity || '');
      showAlert({
        title: 'Rascunho salvo',
        message: 'O documento foi salvo como rascunho com sucesso. Você pode continuar editando ou finalizar quando estiver pronto.',
        variant: 'success',
      });
    } catch (error: any) {
      setError(error.response?.data?.detail || error.message || 'Erro ao salvar rascunho');
    } finally {
      setSaving(false);
    }
  };

  const validateRequiredFields = (): string[] => {
    const missingFields: string[] = [];
    
    // Criar mapa de valores: primeiro de answers, depois de draftValues (draftValues tem prioridade)
    const allValues: Record<number, any> = {};
    
    // Adicionar valores das respostas salvas
    answers.forEach((answer) => {
      allValues[answer.template_field_id] = answer.text_value ?? answer.file_path ?? answer.file_paths ?? '';
    });
    
    // Sobrescrever com valores em draft (valores não salvos ainda)
    Object.keys(draftValues).forEach((key) => {
      allValues[Number(key)] = draftValues[Number(key)];
    });
    
    const shouldValidateField = (field: DocumentTemplateField): boolean =>
      shouldShowConditionalField(field, templateFields, allValues);
    
    templateFields.forEach((field) => {
      // Pular campos de instrução
      if (field.field_type === 'instruction_text') {
        return;
      }
      
      // Verificar se o campo é obrigatório
      if (!field.is_required) {
        return;
      }
      
      // Verificar se o campo deve ser validado (considerando condições)
      if (!shouldValidateField(field)) {
        return;
      }
      
      // Verificar se o campo tem valor
      const value = allValues[field.id];
      let hasValue = false;
      
      if (value !== undefined && value !== null && value !== '') {
        // Para campos que podem ser JSON, verificar se não é apenas estrutura vazia
        if (field.field_type in ['check', 'score', 'currency']) {
          try {
            const parsed = typeof value === 'string' ? JSON.parse(value) : value;
            if (typeof parsed === 'object' && parsed !== null) {
              if (field.field_type === 'currency') {
                hasValue = !!parsed.value;
              } else if (field.field_type === 'check') {
                if (Array.isArray(parsed)) {
                  hasValue = parsed.length > 0;
                } else if (typeof parsed === 'object') {
                  hasValue = Object.keys(parsed).length > 0;
                }
              } else if (field.field_type === 'score') {
                hasValue = !!parsed.ratings && Object.keys(parsed.ratings).length > 0;
              }
            } else {
              hasValue = !!parsed;
            }
          } catch {
            hasValue = typeof value === 'string' ? value.trim().length > 0 : !!value;
          }
        } else if (field.field_type === 'file' || field.field_type === 'files') {
          // Para arquivos, verificar se há resposta salva
          const answer = answers.find(a => a.template_field_id === field.id);
          hasValue = !!(answer?.file_path || answer?.file_paths);
        } else {
          hasValue = typeof value === 'string' ? value.trim().length > 0 : !!value;
        }
      }
      
      if (!hasValue) {
        missingFields.push(field.label || field.name);
      }
    });
    
    return missingFields;
  };

  const handleFinalize = async () => {
    if (!instance) return;

    // Validar campos obrigatórios antes de mostrar confirmação
    const missingFields = validateRequiredFields();
    if (missingFields.length > 0) {
      showAlert({
        title: 'Campos Obrigatórios Não Preenchidos',
        message: 'Não é possível finalizar o documento. Revise os campos pendentes abaixo:',
        variant: 'warning',
        details: missingFields.map(field => `• ${field}`).join('\n')
      });
      return;
    }

    const ok = await showConfirm({
      title: 'Finalizar documento',
      message: 'Tem certeza que deseja finalizar este documento?',
      variant: 'warning',
      confirmLabel: 'Finalizar',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;

    try {
      setSaving(true);
      setError(null);
      await persistDraftFields();
      await api.put(`/api/documents/instances/${instance.id}`, {
        status: 'finalized',
        title: titleDraft || instance.title,
        opportunity: opportunityDraft.trim() || null,
        record_revision: isMemorialDescritivoInstance(instance),
      });
      await reloadInstance();
      setSuccess('Documento finalizado com sucesso');
      setTimeout(() => {
        navigate('/documents');
      }, 2000);
    } catch (error: any) {
      const errorDetail = error.response?.data?.detail || error.message || 'Erro ao finalizar documento';
      showAlert({
        title: 'Erro ao Finalizar Documento',
        message: 'Não foi possível finalizar o documento.',
        variant: 'danger',
        details: typeof errorDetail === 'string' ? errorDetail : JSON.stringify(errorDetail)
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTitleChange = async (title: string) => {
    if (!instance) return;

    try {
      await api.put(`/api/documents/instances/${instance.id}`, { title });
      setInstance((prev) => (prev ? { ...prev, title } : prev));
      setTitleDraft(title);
    } catch (error: any) {
      console.error('Erro ao atualizar título:', error);
    }
  };

  const handleOpportunityChange = async (opportunity: string) => {
    if (!instance) return;

    try {
      const trimmed = opportunity.trim();
      await api.put(`/api/documents/instances/${instance.id}`, {
        opportunity: trimmed || null,
      });
      setInstance((prev) => (prev ? { ...prev, opportunity: trimmed || undefined } : prev));
      setOpportunityDraft(trimmed);
    } catch (error: any) {
      console.error('Erro ao atualizar oportunidade:', error);
    }
  };

  const saveFieldIfChanged = (fieldId: number, nextValue: any, persistedValue: any) => {
    const normalizedNext = typeof nextValue === 'string' ? nextValue : JSON.stringify(nextValue ?? '');
    const normalizedPersisted = typeof persistedValue === 'string' ? persistedValue : JSON.stringify(persistedValue ?? '');
    if (normalizedNext === normalizedPersisted) {
      return;
    }
    handleFieldChange(fieldId, nextValue);
  };

  // Função para obter símbolo da moeda
  const getCurrencySymbol = (currency: string): string => {
    const symbols: { [key: string]: string } = {
      'BRL': 'R$',
      'USD': '$',
      'EUR': '€'
    };
    return symbols[currency] || currency;
  };

  // Função para formatar valor monetário brasileiro
  const formatCurrency = (value: string | number, currency: string = 'BRL'): string => {
    if (!value) return '';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return '';
    const formatted = new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numValue);
    return `${getCurrencySymbol(currency)} ${formatted}`;
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <p>Carregando...</p>
      </div>
    );
  }

  if (!instance) {
    return (
      <div className="container mt-4">
        <Alert variant="danger">Documento não encontrado</Alert>
      </div>
    );
  }

  // Verificar se o usuário pode editar
  const canEdit = currentUser?.role === 'admin' || currentUser?.role === 'analista' || instance.created_by === currentUser?.id;

  // Converter template fields para formato de WorkflowCustomField para usar no CustomFieldsEditor
  const customFields = templateFields.map(field => ({
    id: field.id,
    workflow_id: 0,
    name: field.name,
    label: field.label,
    description: field.description,
    field_type: field.field_type as any,
    options: field.options,
    allow_multiple: field.allow_multiple,
    is_required: field.is_required,
    order: field.order,
    group_name: field.group_name,
    conditional_field_id: field.conditional_field_id,
    conditional_operator: field.conditional_operator,
    conditional_value: field.conditional_value,
    validation_field_id: field.validation_field_id,
    validation_rules: field.validation_rules,
    checkbox_label: field.checkbox_label,
    checkbox_text: field.checkbox_text,
    checkbox_text_trigger_option: field.checkbox_text_trigger_option,
    checkbox_link: field.checkbox_link,
    checkbox_link_text: field.checkbox_link_text,
    created_at: field.created_at,
    updated_at: field.updated_at,
    step_is_required: field.is_required,
    step_is_visible: true,
    step_is_blocked: !canEdit
  }));

  // Converter answers para formato de custom field values
  const fieldValues = answers.map(answer => ({
    id: answer.id,
    document_instance_id: answer.document_instance_id,
    custom_field_id: answer.template_field_id,
    text_value: answer.text_value,
    file_path: answer.file_path,
    file_paths: answer.file_paths,
    created_at: answer.created_at,
    updated_at: answer.updated_at
  }));

  const valueByFieldId: Record<number, any> = fieldValues.reduce((acc, value) => {
    acc[value.custom_field_id] = value.text_value ?? value.file_path ?? value.file_paths ?? '';
    return acc;
  }, {} as Record<number, any>);
  Object.keys(draftValues).forEach((key) => {
    valueByFieldId[Number(key)] = draftValues[Number(key)];
  });

  const shouldShowField = (field: DocumentTemplateField): boolean =>
    shouldShowConditionalField(field, templateFields, valueByFieldId);

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          {instance.document_type?.name || 'Documento'}
          {instance.title && ` - ${instance.title}`}
        </h2>
        <div className="d-flex flex-wrap gap-2 justify-content-end align-items-center">
          {isMemorialDescritivoInstance(instance) && (
            <MemorialDownloadButtons instance={instance} />
          )}
          <Button variant="secondary" onClick={() => navigate('/documents')}>
            Voltar
          </Button>
          {canEdit && (
            <>
              <Button variant="outline-primary" onClick={handleSaveDraft} disabled={saving} className="me-2">
                {saving ? 'Salvando...' : 'Salvar como rascunho'}
              </Button>
              {instance.status !== 'finalized' && (
                <Button variant="success" onClick={handleFinalize} disabled={saving}>
                  {saving ? 'Finalizando...' : 'Finalizar Documento'}
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

      <Card className="mb-4">
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label>Salvar Como</Form.Label>
            <Form.Control
              type="text"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={() => {
                if (titleDraft !== (instance.title || '')) {
                  handleTitleChange(titleDraft);
                }
              }}
              disabled={!canEdit}
              placeholder="Digite um título para o documento"
            />
          </Form.Group>
          <div className="mb-2">
            <strong>Status:</strong>{' '}
            <span className={`badge bg-${instance.status === 'finalized' ? 'success' : 'warning'}`}>
              {instance.status === 'finalized' ? 'Finalizado' : 'Rascunho'}
            </span>
          </div>
          {instance.status === 'finalized' && instance.finalized_at && (
            <div className="mb-2">
              <strong>Finalizado em:</strong>{' '}
              {new Date(instance.finalized_at).toLocaleString('pt-BR')}
            </div>
          )}
          {isMemorialDescritivoInstance(instance) && (
            <>
              <Row className="g-2 mb-2">
                <Col md={4}>
                  <strong>Revisão:</strong> {instance.revision ?? 0}
                </Col>
                <Col md={4}>
                  <strong>Data de emissão:</strong>{' '}
                  {instance.created_at
                    ? new Date(instance.created_at).toLocaleDateString('pt-BR')
                    : '-'}
                </Col>
              </Row>
              <Form.Group className="mb-2">
                <Form.Label>Oportunidade</Form.Label>
                <Form.Control
                  type="text"
                  value={opportunityDraft}
                  onChange={(e) => setOpportunityDraft(e.target.value)}
                  onBlur={() => {
                    if (opportunityDraft !== (instance.opportunity || '')) {
                      handleOpportunityChange(opportunityDraft);
                    }
                  }}
                  disabled={!canEdit}
                  placeholder="Preencher manualmente (ex.: código da oportunidade)"
                />
                <Form.Text className="text-muted">
                  Campo do cabeçalho do template Petronect.
                </Form.Text>
              </Form.Group>
            </>
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <h4>Campos do Documento</h4>
          {templateFields.length === 0 ? (
            <Alert variant="info">Este tipo de documento não possui campos configurados.</Alert>
          ) : (
            templateFields
              .sort((a, b) => a.order - b.order)
              .map((field) => {
                if (!shouldShowField(field)) {
                  return null;
                }

                if (field.field_type === 'instruction_text') {
                  return (
                    <Alert key={field.id} variant="info" className="mb-3">
                      <div className="fw-semibold" style={{ whiteSpace: 'pre-line' }}>
                        {field.label}
                      </div>
                      {field.description && (
                        <div className="mt-1" style={{ whiteSpace: 'pre-line' }}>
                          {field.description}
                        </div>
                      )}
                    </Alert>
                  );
                }

                const answer = answers.find((a) => a.template_field_id === field.id);
                const persistedValue = answer?.text_value || '';
                const value = draftValues[field.id] ?? persistedValue;
                const filePath = answer?.file_path;
                const filePaths = answer?.file_paths;

                return (
                  <Form.Group key={field.id} className="mb-3">
                    <Form.Label>
                      {field.label}
                      {field.is_required && <span className="text-danger"> *</span>}
                    </Form.Label>
                    {field.description && (
                      <Form.Text className="text-muted d-block mb-2" style={{ whiteSpace: 'pre-line', fontSize: '0.875rem', fontStyle: 'italic' }}>
                        {field.description}
                      </Form.Text>
                    )}
                    {field.field_type === 'text_short' && (
                      <Form.Control
                        type="text"
                        value={value}
                        onChange={(e) => {
                          const nextValue = e.target.value;
                          setDraftValues((prev) => ({ ...prev, [field.id]: nextValue }));
                        }}
                        onBlur={() => {
                          const nextValue = draftValues[field.id] ?? persistedValue;
                          saveFieldIfChanged(field.id, nextValue, persistedValue);
                        }}
                        disabled={!canEdit}
                        required={field.is_required}
                      />
                    )}
                    {field.field_type === 'text_long' && (
                      <Form.Control
                        as="textarea"
                        rows={4}
                        value={value}
                        onChange={(e) => {
                          const nextValue = e.target.value;
                          setDraftValues((prev) => ({ ...prev, [field.id]: nextValue }));
                        }}
                        onBlur={() => {
                          const nextValue = draftValues[field.id] ?? persistedValue;
                          saveFieldIfChanged(field.id, nextValue, persistedValue);
                        }}
                        disabled={!canEdit}
                        required={field.is_required}
                      />
                    )}
                    {field.field_type === 'file' && (
                      <div>
                        {filePath && (
                          <div className="mb-2 d-flex flex-wrap align-items-center gap-2">
                            <button
                              type="button"
                              onClick={() => downloadFile(filePath, undefined, {
                                onSessionExpired: () => showAlert({ title: 'Sessão expirada', message: 'Por favor, faça login novamente.', variant: 'warning' }),
                                onError: (msg) => showAlert({ title: 'Erro no download', message: msg, variant: 'danger' }),
                                onFileMissing: () =>
                                  showAlert({
                                    title: 'Arquivo indisponível',
                                    message: 'Este ficheiro não foi encontrado no armazenamento.',
                                    variant: 'warning',
                                  }),
                              })}
                              className="btn btn-sm btn-outline-primary me-2"
                            >
                              Ver arquivo atual
                            </button>
                            {answer?.file_missing && (
                              <span className="badge bg-warning text-dark">Indisponível</span>
                            )}
                          </div>
                        )}
                        <Form.Control
                          type="file"
                          onChange={(e) => {
                            const target = e.target as HTMLInputElement;
                            if (target.files?.[0]) {
                              const selected = target.files[0];
                              const fileError = validateAttachmentFile(selected);
                              if (fileError) {
                                setError(fileError);
                                target.value = '';
                                return;
                              }
                              // Upload do arquivo
                              const formData = new FormData();
                              formData.append('file', selected);
                              api.post(`/api/documents/instances/${instance.id}/answers`, {
                                template_field_id: field.id,
                                file_path: null,
                                text_value: null
                              }).then(() => {
                                // Fazer upload do arquivo separadamente se necessário
                                fetchDocumentInstance();
                              }).catch((error) => {
                                setError(error.response?.data?.detail || 'Erro ao salvar arquivo');
                              });
                            }
                          }}
                          disabled={!canEdit}
                          accept={ATTACHMENT_ACCEPT}
                        />
                      </div>
                    )}
                    {field.field_type === 'files' && (
                      <div>
                        {filePaths && (
                          <div className="mb-2 d-flex flex-column gap-2">
                            {parseCustomFieldFilePathsJson(filePaths).map((entry) => (
                              <div key={entry.fileIndex} className="d-flex flex-wrap gap-2 align-items-center">
                                <button
                                  type="button"
                                  onClick={() =>
                                    downloadFile(entry.path, entry.displayName, {
                                      onSessionExpired: () =>
                                        showAlert({
                                          title: 'Sessão expirada',
                                          message: 'Por favor, faça login novamente.',
                                          variant: 'warning',
                                        }),
                                      onError: (msg) =>
                                        showAlert({ title: 'Erro no download', message: msg, variant: 'danger' }),
                                      onFileMissing: () =>
                                        showAlert({
                                          title: 'Arquivo indisponível',
                                          message: 'Este ficheiro não foi encontrado no armazenamento.',
                                          variant: 'warning',
                                        }),
                                    })
                                  }
                                  className="btn btn-sm btn-outline-primary"
                                >
                                  {entry.displayName}
                                </button>
                                {entry.fileMissing && (
                                  <span className="badge bg-warning text-dark">Indisponível</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        <Form.Control
                          type="file"
                          multiple
                          onChange={(e) => {
                            const target = e.target as HTMLInputElement;
                            if (target.files && target.files.length > 0) {
                              const selectedFiles = Array.from(target.files);
                              const filesError = validateAttachmentFiles(selectedFiles);
                              if (filesError) {
                                setError(filesError);
                                target.value = '';
                                return;
                              }
                              // Mantém comportamento atual da tela para registrar resposta de múltiplos arquivos
                              // (upload físico pode ser evoluído para endpoint dedicado em próxima etapa)
                              const filenames = selectedFiles.map((f) => f.name);
                              handleFieldChange(field.id, filenames);
                            }
                          }}
                          disabled={!canEdit}
                          accept={ATTACHMENT_ACCEPT}
                        />
                      </div>
                    )}
                    {field.field_type === 'date' && (
                      <Form.Control
                        type="date"
                        value={value}
                        onChange={(e) => {
                          const nextValue = e.target.value;
                          setDraftValues((prev) => ({ ...prev, [field.id]: nextValue }));
                        }}
                        onBlur={() => {
                          const nextValue = draftValues[field.id] ?? persistedValue;
                          saveFieldIfChanged(field.id, nextValue, persistedValue);
                        }}
                        disabled={!canEdit}
                        required={field.is_required}
                      />
                    )}
                    {(field.field_type === 'integer' || field.field_type === 'decimal') && (
                      <Form.Control
                        type="number"
                        step={field.field_type === 'decimal' ? '0.01' : '1'}
                        value={value}
                        onChange={(e) => {
                          const nextValue = e.target.value;
                          setDraftValues((prev) => ({ ...prev, [field.id]: nextValue }));
                        }}
                        onBlur={() => {
                          const nextValue = draftValues[field.id] ?? persistedValue;
                          saveFieldIfChanged(field.id, nextValue, persistedValue);
                        }}
                        disabled={!canEdit}
                        required={field.is_required}
                      />
                    )}
                    {field.field_type === 'currency' && (() => {
                      let currencyValue: { currency: string; value: string } = { currency: 'BRL', value: '' };
                      if (value) {
                        try {
                          currencyValue = typeof value === 'string'
                            ? JSON.parse(value)
                            : value;
                        } catch {
                          currencyValue = { currency: 'BRL', value: String(value) };
                        }
                      }

                      return (
                        <div className="d-flex gap-2 align-items-center">
                          <Form.Select
                            value={currencyValue.currency || 'BRL'}
                            onChange={(e) => {
                              const newCurrencyValue = { ...currencyValue, currency: e.target.value };
                              setDraftValues((prev) => ({ ...prev, [field.id]: JSON.stringify(newCurrencyValue) }));
                              handleFieldChange(field.id, JSON.stringify(newCurrencyValue));
                            }}
                            disabled={!canEdit}
                            style={{ width: '160px' }}
                          >
                            <option value="BRL">BRL (R$)</option>
                            <option value="USD">USD ($)</option>
                            <option value="EUR">EUR (€)</option>
                          </Form.Select>
                          <Form.Control
                            type="text"
                            inputMode="decimal"
                            value={formatCurrencyInputDisplay(
                              currencyValue.value || '',
                              currencyValue.currency || 'BRL'
                            )}
                            onChange={(e) => {
                              const normalized = normalizeCurrencyInput(e.target.value);
                              if (normalized === null) return;
                              const newCurrencyValue = { ...currencyValue, value: normalized };
                              setDraftValues((prev) => ({ ...prev, [field.id]: JSON.stringify(newCurrencyValue) }));
                            }}
                            onBlur={() => {
                              const nextValue = draftValues[field.id] ?? persistedValue;
                              saveFieldIfChanged(field.id, nextValue, persistedValue);
                            }}
                            disabled={!canEdit}
                            required={field.is_required}
                            placeholder="Ex.: 30000,01"
                          />
                          {currencyValue.value && (
                            <Form.Text className="text-muted" style={{ whiteSpace: 'nowrap' }}>
                              {formatCurrency(currencyValue.value, currencyValue.currency)}
                            </Form.Text>
                          )}
                        </div>
                      );
                    })()}
                    {(field.field_type === 'list' || field.field_type === 'combo') && (
                      <Form.Select
                        value={value}
                        onChange={(e) => {
                          const nextValue = e.target.value;
                          setDraftValues((prev) => ({ ...prev, [field.id]: nextValue }));
                          handleFieldChange(field.id, nextValue);
                        }}
                        disabled={!canEdit}
                        required={field.is_required}
                      >
                        <option value="">Selecione...</option>
                        {field.options?.split(/[,\n]/).map((opt, idx) => (
                          <option key={idx} value={opt.trim()}>
                            {opt.trim()}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                    {field.field_type === 'check' && (
                      (() => {
                        const options = (field.options || '')
                          .split(/[,\n]/)
                          .map((opt) => opt.trim())
                          .filter((opt) => opt.length > 0);
                        const hasOptions = options.length > 0;

                        if (hasOptions) {
                          let selectedValues: string[] = [];
                          if (value) {
                            try {
                              const parsed = JSON.parse(value);
                              if (Array.isArray(parsed)) {
                                selectedValues = parsed.map((v) => String(v));
                              } else if (parsed !== null && parsed !== undefined && String(parsed).trim() !== '') {
                                selectedValues = [String(parsed)];
                              }
                            } catch {
                              selectedValues = String(value).trim() ? [String(value)] : [];
                            }
                          }

                          const selectedValue = selectedValues[0] || '';
                          return (
                            <div>
                              {options.map((opt, idx) => (
                                <Form.Check
                                  key={`${field.id}-${idx}`}
                                  type={field.allow_multiple ? 'checkbox' : 'radio'}
                                  name={field.allow_multiple ? undefined : `field-${field.id}`}
                                  label={opt}
                                  checked={field.allow_multiple ? selectedValues.includes(opt) : selectedValue === opt}
                                  disabled={!canEdit}
                                  onChange={(e) => {
                                    if (field.allow_multiple) {
                                      const next = e.target.checked
                                        ? [...selectedValues, opt]
                                        : selectedValues.filter((v) => v !== opt);
                                      setDraftValues((prev) => ({ ...prev, [field.id]: JSON.stringify(next) }));
                                      handleFieldChange(field.id, next);
                                      return;
                                    }
                                    const nextValue = e.target.checked ? opt : '';
                                    setDraftValues((prev) => ({ ...prev, [field.id]: nextValue }));
                                    handleFieldChange(field.id, nextValue);
                                  }}
                                  className="mb-1"
                                />
                              ))}
                            </div>
                          );
                        }

                        return (
                          <Form.Check
                            className="custom-field-check"
                            type="checkbox"
                            checked={value === 'true' || value === '1'}
                            onChange={(e) => {
                              const nextValue = e.target.checked ? 'true' : 'false';
                              setDraftValues((prev) => ({ ...prev, [field.id]: nextValue }));
                              handleFieldChange(field.id, nextValue);
                            }}
                            disabled={!canEdit}
                            label={field.checkbox_label || field.label}
                          />
                        );
                      })()
                    )}
                    {field.field_type === 'score' && (
                      <ScoreField
                        field={field}
                        value={value}
                        canEdit={canEdit}
                        onFieldChange={handleFieldChange}
                      />
                    )}
                  </Form.Group>
                );
              })
          )}
          {canEdit && instance.status !== 'finalized' && (
            <div className="mt-4 d-flex justify-content-end gap-2">
              <Button variant="outline-primary" onClick={handleSaveDraft} disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar como rascunho'}
              </Button>
              <Button variant="success" onClick={handleFinalize} disabled={saving}>
                {saving ? 'Finalizando...' : 'Finalizar Documento'}
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default DocumentEdit;
